import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SignOut, Gauge, Gear, Link as LinkIcon, FileText, CurrencyDollar, Users, List, Bank, ChartBar, Buildings, PencilSimple, ChartLine, ChartLineUp, Pulse } from "@phosphor-icons/react";

function SidebarContent({ items, pathname, user, logout, onClose }) {
  return (
    <>
      <div className="p-5 border-b border-[#123a5e]">
        <div className="flex flex-col items-center gap-1">
          <img src="/logo.png" alt="BRFertil" className="w-20 h-20 object-contain" />
          <span className="text-[10px] tracking-widest text-[#6b89a8] uppercase">Trava de Câmbio</span>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          const active = pathname === it.to;
          return (
            <Link
              key={it.to}
              to={it.to}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${active ? "bg-[#40B6A2]/10 text-[#40B6A2] border-l-2 border-[#40B6A2]" : "text-[#8aa9c9] hover:bg-[#0B2D4D] hover:text-white"}`}
            >
              <Icon className="w-4 h-4" weight={active ? "fill" : "regular"} />
              {it.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-[#123a5e]">
        <div className="px-3 py-2 mb-2">
          <p className="text-xs text-[#6b89a8] tracking-wider uppercase">{user?.role}</p>
          <p className="text-sm font-medium truncate">{user?.nome}</p>
          <p className="text-xs text-[#6b89a8] truncate">{user?.email}</p>
        </div>
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8aa9c9] hover:text-white hover:bg-[#0B2D4D] rounded-sm transition-colors">
          <SignOut className="w-4 h-4" />
          Sair
        </button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  const navByRole = {
    vendedor: [
      { to: "/vendedor", icon: CurrencyDollar, label: "Cotação & Trava" },
      { to: "/vendedor/travas", icon: LinkIcon, label: "Travas Pendentes" },
      { to: "/vendedor/ptax", icon: Bank, label: "PTAX" },
    ],
    gestao: [
      { to: "/hedge", icon: Gauge, label: "Dashboard" },
      { to: "/hedge/posicao", icon: ChartBar, label: "Posição do Dia" },
      { to: "/hedge/trava-manual", icon: PencilSimple, label: "Trava Manual" },
      { to: "/vendedor/ptax", icon: Bank, label: "PTAX" },
      { to: "/charts", icon: ChartLineUp, label: "Gráficos" },
    ],
    hedge: [
      { to: "/hedge", icon: Gauge, label: "Dashboard Hedge" },
      { to: "/hedge/posicao", icon: ChartBar, label: "Posição do Dia" },
      { to: "/hedge/travas", icon: LinkIcon, label: "Travas" },
      { to: "/hedge/relatorio", icon: FileText, label: "Relatório Margem" },
      { to: "/hedge/trava-manual", icon: PencilSimple, label: "Trava Manual" },
      { to: "/admin/parametros", icon: Gear, label: "Parâmetros" },
      { to: "/vendedor", icon: CurrencyDollar, label: "Cotação" },
      { to: "/vendedor/ptax", icon: Bank, label: "PTAX" },
      { to: "/hedge/exposicao", icon: ChartLine, label: "Exposição" },
      { to: "/charts", icon: ChartLineUp, label: "Gráficos" },
    ],
    admin: [
      { to: "/hedge", icon: Gauge, label: "Dashboard Hedge" },
      { to: "/hedge/posicao", icon: ChartBar, label: "Posição do Dia" },
      { to: "/hedge/travas", icon: LinkIcon, label: "Travas" },
      { to: "/hedge/relatorio", icon: FileText, label: "Relatório Margem" },
      { to: "/hedge/trava-manual", icon: PencilSimple, label: "Trava Manual" },
      { to: "/admin/parametros", icon: Gear, label: "Parâmetros" },
      { to: "/admin/usuarios", icon: Users, label: "Usuários" },
      { to: "/admin/clientes", icon: Buildings, label: "Grupos SAP" },
      { to: "/vendedor", icon: CurrencyDollar, label: "Cotação" },
      { to: "/vendedor/ptax", icon: Bank, label: "PTAX" },
      { to: "/hedge/exposicao", icon: ChartLine, label: "Exposição" },
      { to: "/charts", icon: ChartLineUp, label: "Gráficos" },
      { to: "/hedge/spread-sombra", icon: Pulse, label: "Spread Sombra" },
    ],
  };

  const items = navByRole[user?.role] || [];
  const sidebarProps = { items, pathname: location.pathname, user, logout, onClose: () => setOpen(false) };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#061729", color: "white" }}>

      {/* Sidebar desktop */}
      {!isMobile && (
        <aside style={{ width: 256, borderRight: "1px solid #123a5e", background: "#061729", display: "flex", flexDirection: "column" }}>
          <SidebarContent {...sidebarProps} />
        </aside>
      )}

      {/* Drawer mobile */}
      {isMobile && open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex" }}>
          <div style={{ width: 256, background: "#061729", borderRight: "1px solid #123a5e", display: "flex", flexDirection: "column" }}>
            <SidebarContent {...sidebarProps} />
          </div>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.5)" }} onClick={() => setOpen(false)} />
        </div>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header mobile */}
        {isMobile && (
          <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #123a5e", background: "#061729" }}>
            <button onClick={() => setOpen(true)} style={{ color: "white", background: "none", border: "none", cursor: "pointer" }}>
              <List size={24} />
            </button>
            <span style={{ fontSize: 14, fontWeight: "bold" }}>BRFERTIL · Trava de Câmbio</span>
          </header>
        )}
        <main style={{ flex: 1, overflowY: "auto" }}>{children}</main>
      </div>
    </div>
  );
}
